import '@splidejs/splide/css';
import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import { Intersection } from '@splidejs/splide-extension-intersection';
import gsap from 'gsap';

const personSliders = document.querySelectorAll('.wp-block-techgala-person-list .splide');

personSliders.forEach((slider) => {
	const block = slider.closest('.wp-block-techgala-person-list');
	const useSpSlider = block ? block.dataset.spSlider !== 'false' : true;
	const usePcSlider = block ? block.dataset.pcSlider === 'true' : false;

	const tabletBreakpoint = useSpSlider ? { destroy: false } : { destroy: true };
	const mobileBreakpoint = useSpSlider
		? {
			fixedWidth : 150,
			gap : 18,
			perMove: 1,
			flickMaxPages: 1,
			flickPower: 150,
			snap: true,
		}
		: { destroy: true };

	let personSlider = new Splide(slider, {
		type : 'slide',
		gap : 18,
		pagination : false,
		arrows : true,
		autoWidth : false,
		fixedWidth : 210,
		omitEnd: true,
		focus : 0,
		destroy : ! usePcSlider,
		padding: { left: 20, right: 20 },
		breakpoints: {
			1179: tabletBreakpoint,
			1019: mobileBreakpoint,
		},
	});

	const bar = personSlider.root.querySelector( '.person-carousel-progress-bar' );

	personSlider.on( 'mounted moved resized updated', function () {
		const end  = personSlider.Components.Controller.getEnd() + 1;
		const rate = Math.min( ( personSlider.index + 1 ) / end, 1 );
		bar.style.width = String( 100 * rate ) + '%';
	} );
	personSlider.mount();
});

const galleryCarousels = document.querySelectorAll(
	'.wp-block-gallery.is-style-1col-carousel.splide, .wp-block-gallery.is-style-small-col-carousel.splide'
);

galleryCarousels.forEach( ( slider ) => {
	const isSmallColCarousel = slider.classList.contains( 'is-style-small-col-carousel' );

	const gallerySlider = new Splide( slider, isSmallColCarousel
		? {
			type: 'slide',
			gap: 20,
			pagination: slider.classList.contains( 'nav-pc-dots' ),
			arrows: true,
			omitEnd: true,
			focus: 0,
			fixedWidth: 740,
			breakpoints: {
				1019: {
					pagination: slider.classList.contains( 'nav-sp-dots' ),
					fixedWidth: 300,
					gap: 18,
				},
			},
		}
		: {
			type: 'slide',
			rewind: true,
			perPage: 1,
			gap: 0,
			pagination: slider.classList.contains( 'nav-pc-dots' ),
			arrows: true,
			breakpoints: {
				1019: {
					rewind: false,
					pagination: slider.classList.contains( 'nav-sp-dots' ),
					fixedWidth: 300,
					gap: 18,
					focus: 0,
					omitEnd: true,
				},
			},
		}
	);

	const bar = gallerySlider.root.querySelector( '.gallery-carousel-progress-bar' );
	if ( bar ) {
		gallerySlider.on( 'mounted moved resized updated', function () {
			const end  = gallerySlider.Components.Controller.getEnd() + 1;
			const rate = Math.min( ( gallerySlider.index + 1 ) / end, 1 );
			bar.style.width = String( 100 * rate ) + '%';
		} );
	}

	gallerySlider.mount();
} );

function bindGalleryCarouselProgressBar( splideInstance ) {
	const bar = splideInstance.root.querySelector( '.gallery-carousel-progress-bar' );
	if ( ! bar ) {
		return;
	}
	splideInstance.on( 'mounted moved resized updated', function () {
		const end  = splideInstance.Components.Controller.getEnd() + 1;
		const rate = Math.min( ( splideInstance.index + 1 ) / end, 1 );
		bar.style.width = String( 100 * rate ) + '%';
	} );
}

const cardCarousels = document.querySelectorAll( '.card-carousel.splide' );

const cardCarouselBaseOptions = ( slider ) => ( {
	type: 'slide',
	gap: 20,
	pagination: slider.classList.contains( 'nav-pc-dots' ),
	arrows: true,
	omitEnd: true,
	focus: 0,
} );

cardCarousels.forEach( ( slider ) => {
	let options;
	const baseOptions = cardCarouselBaseOptions( slider );
	const spPagination = slider.classList.contains( 'nav-sp-dots' );

	if ( slider.classList.contains( 'is-size-small' ) ) {
		options = {
			...baseOptions,
			fixedWidth: 280,
			gap: 24,
			breakpoints: {
				1019: { fixedWidth: false, perPage: 2, gap: 10, pagination: spPagination },
			},
		};
	} else if ( slider.classList.contains( 'is-size-wide' ) ) {
		options = {
			...baseOptions,
			perPage: 1,
			gap: 30,
			breakpoints: {
				1019: { gap: 30, pagination: spPagination },
			},
		};
	} else if ( slider.classList.contains( 'is-size-custom' ) ) {
		const customStyles = getComputedStyle( slider );
		const customWidthPc = customStyles.getPropertyValue( '--card-carousel-width-pc' ).trim() || '570px';
		const customWidthSp = customStyles.getPropertyValue( '--card-carousel-width-sp' ).trim()
			|| 'max(350px, calc(100dvw - 40px))';
		options = {
			...baseOptions,
			fixedWidth: customWidthPc,
			gap: 24,
			breakpoints: {
				1019: {
					fixedWidth: customWidthSp,
					gap: 10,
					pagination: spPagination,
				},
			},
		};
	} else {
		options = {
			...baseOptions,
			fixedWidth: 570,
			gap: 24,
			breakpoints: {
				1019: {
					fixedWidth: 'max(350px, calc(100dvw - 40px))',
					gap: 10,
					pagination: spPagination,
				},
			},
		};
	}

	const cardCarouselSlider = new Splide( slider, options );
	bindGalleryCarouselProgressBar( cardCarouselSlider );
	cardCarouselSlider.mount();
} );

const galleryTracks = document.querySelectorAll( '.gallery-auto-scroll .slide__track' );

const GALLERY_SCROLL_SPEED = 80; // px/秒 — この値でスピードを調整

const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

galleryTracks.forEach( ( track ) => {
    const gallery = track.querySelector( '.wp-block-gallery' );
    if ( ! gallery ) {
        return;
    }

    const galleryWidth = gallery.getBoundingClientRect().width;
    if ( ! galleryWidth ) {
        return;
    }
    const clonesNeeded = Math.max( 0, Math.ceil( window.innerWidth / galleryWidth ) - 1 );
    if ( clonesNeeded > 0 ) {
        const fragment = document.createDocumentFragment();
        for ( let i = 0; i < clonesNeeded; i++ ) {
            fragment.appendChild( gallery.cloneNode( true ) );
        }
        track.appendChild( fragment );
    }

    if ( prefersReducedMotion ) {
        return;
    }

    const trackClone = track.cloneNode( true );
    track.parentElement.appendChild( trackClone );

    const totalWidth = track.offsetWidth + 20;
    const duration = totalWidth / GALLERY_SCROLL_SPEED;

    gsap.set( track, { x: 0 } );
    gsap.set( trackClone, { x: 0 } );

    gsap.to( track, {
        x: -totalWidth,
        duration,
        ease: 'none',
        repeat: -1,
    } );

    gsap.to( trackClone, {
        x: -totalWidth,
        duration,
        ease: 'none',
        repeat: -1,
        onRepeat: () => gsap.set( trackClone, { x: totalWidth } ),
    } );
} );
